import { z } from "zod";

export const GeoLayerScopeSchema = z.enum(["ALL", "GROUPS"]);
export type GeoLayerScope = z.infer<typeof GeoLayerScopeSchema>;

export const GeoLayerStatusSchema = z.enum(["processing", "ready", "error"]);
export type GeoLayerStatus = z.infer<typeof GeoLayerStatusSchema>;

const ColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Couleur invalide (format #rrggbb)");
export const GEO_LAYER_ICONS = ["circle", "square", "triangle", "diamond", "star", "pin"] as const;
export const GeoLayerIconSchema = z.enum(GEO_LAYER_ICONS);
export type GeoLayerIcon = z.infer<typeof GeoLayerIconSchema>;

/** Style de dessin d'une couche : épaisseur des lignes/contours, taille des points, opacité du remplissage et icône des points. */
export const GeoLayerStyleSchema = z.object({
  line_width: z.number().min(0.5).max(12),
  point_size: z.number().min(2).max(24),
  fill_opacity: z.number().min(0).max(1),
  icon: GeoLayerIconSchema,
  /** Regroupe les points proches en grappes quand la carte est éloignée. */
  cluster: z.boolean(),
});
export type GeoLayerStyle = z.infer<typeof GeoLayerStyleSchema>;

export const DEFAULT_GEO_LAYER_STYLE: GeoLayerStyle = { line_width: 1.5, point_size: 3, fill_opacity: 0.25, icon: "circle", cluster: true };

/** Lit un style stocké (JSON libre) : toute valeur absente ou invalide retombe sur le défaut. */
export function parseGeoLayerStyle(value: unknown): GeoLayerStyle {
  const partial = GeoLayerStyleSchema.partial().safeParse(value && typeof value === "object" ? value : {});
  return { ...DEFAULT_GEO_LAYER_STYLE, ...(partial.success ? partial.data : {}) };
}

const BboxSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]);

/** Couche telle que la voit un utilisateur : aucun chemin de stockage, aucune information d'administration. */
export const GeoLayerUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  default_color: z.string(),
  style: GeoLayerStyleSchema,
  bbox: BboxSchema.nullable(),
  feature_count: z.number(),
  geometry_counts: z.record(z.string(), z.number()),
  property_keys: z.array(z.string()),
  /** Taille du fichier d'origine (octets) : sert à afficher la progression du téléchargement. */
  size_bytes: z.number(),
  /** Change à chaque remplacement du fichier : clé de cache de l'URL de contenu. */
  version: z.number(),
  sha256: z.string(),
  /** Choix de l'utilisateur ; faux tant qu'il n'a pas demandé l'affichage. */
  visible: z.boolean(),
  /** Vrai si la couche n'est pas encore publiée (visible seulement par les administrateurs). */
  is_draft: z.boolean(),
});
export type GeoLayerUser = z.infer<typeof GeoLayerUserSchema>;

/** Couche telle que la voit un administrateur. */
export const GeoLayerAdminSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  original_filename: z.string(),
  size_bytes: z.number(),
  stored_bytes: z.number(),
  feature_count: z.number(),
  dropped_count: z.number(),
  geometry_counts: z.record(z.string(), z.number()),
  property_keys: z.array(z.string()),
  bbox: BboxSchema.nullable(),
  default_color: z.string(),
  style: GeoLayerStyleSchema,
  sort_order: z.number(),
  status: GeoLayerStatusSchema,
  error: z.string().nullable(),
  /** Vrai si un fichier a déjà été traité avec succès (il est alors servi, même si un remplacement est en cours ou a échoué). */
  has_content: z.boolean(),
  is_published: z.boolean(),
  scope: GeoLayerScopeSchema,
  groupe_ids: z.array(z.number()),
  version: z.number(),
  created_by: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type GeoLayerAdmin = z.infer<typeof GeoLayerAdminSchema>;

export const GeoLayerUpdateSchema = z
  .object({
    name: z.string().trim().min(1, "Le nom est requis").max(255),
    description: z.string().trim().max(2000).nullable(),
    default_color: ColorSchema,
    style: GeoLayerStyleSchema,
    sort_order: z.number().int().min(-100000).max(100000),
    is_published: z.boolean(),
    scope: GeoLayerScopeSchema,
    groupe_ids: z.array(z.number().int().positive()).max(1000),
  })
  .partial();
export type GeoLayerUpdate = z.infer<typeof GeoLayerUpdateSchema>;

/** Champs texte envoyés avec le fichier lors du dépôt (multipart). */
export const GeoLayerUploadFieldsSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  description: z.string().trim().max(2000).optional(),
});
export type GeoLayerUploadFields = z.infer<typeof GeoLayerUploadFieldsSchema>;

export const GeoLayerPreferenceSchema = z.object({ visible: z.boolean() });
export type GeoLayerPreference = z.infer<typeof GeoLayerPreferenceSchema>;

/** Taille maximale d'un fichier de couche : 1 Gio. */
export const GEO_LAYER_MAX_BYTES = 1024 * 1024 * 1024;
